import {useState, useCallback, useEffect, useMemo} from 'react';
import produce from 'immer';
import {useSMCSnackbar} from '../../../utils/hooks';
import {useTranslation} from 'react-i18next';
import {setProperty, getErrorCode, isStringI18n, extractI18nString} from '../../../utils/utils';
import Api from '../../../api/SMPFetchHelper';
import {ObjectTypes, ApiErrorCodes, ErrorCodes} from '../../../api/consts';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import store from '../../../store';
import {push} from 'connected-react-router';
import {SMCPaths} from '../../../conf/Routes';
import {ImageGetSchema} from '../../../api/DBSchema/Image';
import generalIcon from '../../../assets/images/general.png';
import historyIcon from '../../../assets/images/history.png';
import ImageGeneral from './InnerPages/ImageGeneral';
import ImageHistory from './InnerPages/ImageHistory';
import {helpContextPageUrl} from '../../../conf/Routes';

export function useImageEditor(imageId, defaultChosenPageId) {
    const { t } = useTranslation();
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar, enqueueLogoutSnackbar } = useSMCSnackbar();
    const { getErrorMessage, getMessagesArrayByPages } = useSMCErrorHandler();

    const [origImageObject, setOrigImageObject] = useState({});
    const [imageObject, setImageObject] = useState({});

    const [isPageDataLoaded, setIsPageDataLoaded] = useState(false);
    const [isInnerPageLoading, setIsInnerPageLoading] = useState(false);
    const [isPageLoadFailed, setIsPageLoadFailed] = useState(false);
    const [errorMessagesByPageList, setErrorMessagesByPageList] = useState(null);

    const [isObjectDirty, setIsObjectDirty] = useState(false);

    const [chosenPageId, setChosenPageId] = useState(defaultChosenPageId || 'general');

    const editorPages = useMemo(() => ([
        { id: ImageGetSchema.pages.general.pageId, title: t('SMP.GENERAL'), icon: generalIcon, pageElement: ImageGeneral, helpId: helpContextPageUrl.imageGeneral },        
        { id: ImageGetSchema.pages.history.pageId, title: t('SMP.COMMUNITIES.TABLE.INNER_MENU.HISTORY'), icon: historyIcon, pageElement: ImageHistory, helpId: helpContextPageUrl.imageHistory }        
    ]), [t]);
    
    const [pageIdToPageName, pageIdToHelpId] = useMemo(() => {
        const _pageIdToPageName = {};
        const _pageIdToHelpId = {};
        const addPagesData = (pagesRoot) => {
            pagesRoot.forEach(page => {
                if (page.id) {
                    _pageIdToPageName[page.id] = page.title;
                    _pageIdToHelpId[page.id] = page.helpId;
                }
                if (page.children) {
                    addPagesData(page.children);
                }
            })
        }
        addPagesData(editorPages);
        return [_pageIdToPageName, _pageIdToHelpId];
    }, [editorPages]);

    const helpId = useMemo(() => pageIdToHelpId[chosenPageId], [pageIdToHelpId, chosenPageId]);

    const fetchImage = useCallback(async () => {
        setIsPageDataLoaded(false);
        try {
            const fetchImageResp = await Api.fetchSingleObject(ObjectTypes.IMAGE, imageId);
            const image = fetchImageResp.data.data.image;
            // Validate server response
            if (!image) {
                console.error('Get image response failed: image response returned empty');
                throw new Error(ErrorCodes.SERVER_ERROR);
            }
            setOrigImageObject(image);
            setImageObject(image);
            setIsPageLoadFailed(false);
            setIsObjectDirty(false); // Just fetched, not dirty
        } catch (e) {
            const errorCode = getErrorCode(e) || ApiErrorCodes.GENERAL_ERROR;
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                enqueueLogoutSnackbar(t(errorCode));
            } else {
                enqueueErrorSnackbar(t(errorCode));
            }
            setIsPageLoadFailed(true);
        }
        setIsPageDataLoaded(true);
    }, [imageId, enqueueErrorSnackbar, enqueueLogoutSnackbar, t]);

    const setImageData = useCallback((paramPath, newValue) => {
        setIsObjectDirty(true); // Data changed, object is dirty
        setImageObject(imageObj => produce(imageObj, draft => {
            setProperty(draft, paramPath, newValue);
        }));
    }, []);

    const verifyData = useCallback(() => {
        setIsInnerPageLoading(true);
        Api.verifySingleObject(ObjectTypes.IMAGE, imageId, imageObject)
        .then(() => {
            // On no error, clear error message
            setErrorMessagesByPageList(null);
        })
        .catch((error) => {
            const errorMessageArrayByPage = getMessagesArrayByPages(error, pageIdToPageName);
            if (errorMessageArrayByPage) {
                setErrorMessagesByPageList(errorMessageArrayByPage);
            }
        })
        .finally(() => {
            setIsInnerPageLoading(false);
        });
    }, [imageId, getMessagesArrayByPages, imageObject, pageIdToPageName]);

    const onPageChange = useCallback((pageId) => {
        verifyData();
        setChosenPageId(pageId);
    }, [verifyData]);

    const saveButtonOnClick = useCallback(() => {
        Api.updateSingleObject(ObjectTypes.IMAGE, imageId, imageObject)
        .then(() => {
            setIsPageDataLoaded(false);
            setErrorMessagesByPageList(null);
            fetchImage();
            enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_SAVED_OK'));
        })
        .catch((error) => {
            const errorMessage = getErrorMessage(error);
            alert(errorMessage);
        })
        .finally(() => {
            setIsPageDataLoaded(true);
        });
    }, [fetchImage, getErrorMessage, imageId, imageObject, enqueueSuccessSnackbar, t]);

    const deleteButtonOnClick = useCallback(() => {
        if (window.confirm(t('OBJECT_EDITOR_OBJ_DELETE', [imageId] ))) {
            Api.deleteObjects(ObjectTypes.IMAGE, [imageId])
            .then(res => {
                const deleteResult = res.data.results[0];
                if (deleteResult.success) {
                    setIsObjectDirty(false); // Set as false to not trigger dirty modal
                    store.dispatch(push(SMCPaths.images));
                    enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_DELETE_OK', [imageId]));
                } else {
                    const deleteErrorCode = deleteResult.errorCode;
                    let deleteErrorParams;
                    if (deleteResult.errorParams) {
                        deleteErrorParams = deleteResult.errorParams.map(param => (
                            isStringI18n(param)) ? t(extractI18nString(param)) : param
                        );
                    }
                    enqueueErrorSnackbar(t(extractI18nString(deleteErrorCode), deleteErrorParams));
                }
            })
            .catch(err => {
                const errorCode = getErrorMessage(err);
                enqueueErrorSnackbar(t(errorCode));
            });
        }
    }, [imageId, getErrorMessage, enqueueSuccessSnackbar, enqueueErrorSnackbar, t]);

    const revertButtonOnClick = useCallback(() => {
        setImageObject(origImageObject);
        setIsObjectDirty(false); // Is not dirty, object is reset to originl values
        setErrorMessagesByPageList(null);
    }, [origImageObject]);

    // Fetch image on loading of page
    useEffect(() => {
        fetchImage();
    }, [fetchImage]);
    
    return {
        imageObject,
        isObjectDirty,
        editorPages,
        helpId,
        isPageDataLoaded,
        isInnerPageLoading,
        isPageLoadFailed,
        chosenPageId,
        errorMessagesByPageList,
        onPageChange,
        setImageData,
        saveButtonOnClick,
        deleteButtonOnClick,
        revertButtonOnClick,
        reloadPageData: fetchImage
    };
}